import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref } from 'vue';
import { faCaretDown, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import BasicButton from '@/ui-components/BasicButton.vue';
import BaseModal from '@/components/modals/BaseModal.vue';
import OfferReporterSuccessModal from '@/components/modals/offer-reporter/OfferReporterSuccessModal.vue';

import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery, useMutation } from '@/helpers/composables/useApollo';
import { useConstantStore, useLanguageStore } from '@/helpers/composables/useStores';

import { ModalHelper } from '@/helpers/modal-helper';
import { ModalHelper as ModalHelperVFM } from '@/helpers/modal-helper-vfm';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import Provider from '@/interfaces/provider';

import {
	Episode,
	Movie,
	MovieContent,
	ObjectType,
	Offer,
	Platform,
	Season,
	SeasonContent,
	Show,
	ShowContent,
	UpcomingRelease,
} from '@/@types/graphql-types';
import {
	ReportFeedbackDocument,
	ReportFeedbackMutation,
	ReportFeedbackMutationVariables,
} from '@/graphql/mutation/ReportFeedback.mutation';
import { SnowplowContext } from '@/helpers/tracking/providers';
import {
	GetSportProvidersByShortNamesDocument,
	GetSportProvidersByShortNamesQuery,
	GetSportProvidersByShortNamesQueryVariables,
} from '@/features/sports/graphql/queries/GetSportProvidersByShortNames.query';
import {
	GetTitleProvidersByShortNamesDocument,
	GetTitleProvidersByShortNamesQuery,
	GetTitleProvidersByShortNamesQueryVariables,
} from '@/pages/graphql/queries/GetTitleProvidersByShortNames.query';
import { APP_PLATFORM, APP_VERSION } from '@/stores/app.store';

export type OfferReporterModalTitle = Pick<Movie | Show | Season | Episode, 'objectId' | 'objectType'> &
	Pick<MovieContent | ShowContent | SeasonContent, 'scoring'>;


export default /*#__PURE__*/_defineComponent({
  __name: 'OfferReporterModal',
  props: {
    titleId: null,
    titleObjectId: null,
    titleObjectType: null,
    titleName: null,
    originalReleaseYear: null,
    titleOffers: { default: () => [] },
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { titleId: string, titleObjectId: number, titleObjectType:
			| ObjectType.Movie
			| ObjectType.ShowSeason
			| ObjectType.Show
			| ObjectType.ShowEpisode
			| ObjectType.SportEvent, titleName: string, originalReleaseYear?: number, titleOffers: Offer[] | UpcomingRelease[], additionalContexts: SnowplowContext[] };

const issueName = {
	mismatch: 'MISMATCH',
	title_not_available: 'TITLE_NOT_AVAILABLE',
	missing_offer: 'MISSING_OFFER',
	wrong_price: 'WRONG_OFFER',
	wrong_info: 'WRONG_INFO',
	bug: 'BUG',
	other: 'OTHER',
};



const { t, tc } = useI18n();
const { providers } = useConstantStore();
const { language, country, webLocale } = useLanguageStore();
const modal = ref<InstanceType<typeof BaseModal> | null>(null);

const ionItemData = reactive({
	commentInput: '',
	emailInput: '',
	errorNotification: false,
	externalLinkInput: '',
	isSending: false,
	issueSelected: '',
	providerSelected: '',
	providerSelectedClear: '',
	stayUpdated: false,
});

const queryVariables = computed<GetSportProvidersByShortNamesQueryVariables>(() => ({
	country: country.value,
	platform: Platform.Web,
}));

const sportProvidersByShortNamesQueryOptions = computed(() => ({
	enabled: props.titleObjectType === ObjectType.SportEvent,
}));
const { result: sportProvidersByShortNamesData } = useQuery<
	GetSportProvidersByShortNamesQuery,
	GetSportProvidersByShortNamesQueryVariables
>(GetSportProvidersByShortNamesDocument, queryVariables, sportProvidersByShortNamesQueryOptions);
const sportProvidersByShortName = computed(() =>
	(sportProvidersByShortNamesData.value?.packages || []).map(({ shortName }) => shortName)
);

const titleProvidersByShortNamesQueryOptions = computed(() => ({
	enabled: props.titleObjectType !== ObjectType.SportEvent,
}));
const { result: titleProvidersByShortNamesData } = useQuery<
	GetTitleProvidersByShortNamesQuery,
	GetTitleProvidersByShortNamesQueryVariables
>(GetTitleProvidersByShortNamesDocument, queryVariables, titleProvidersByShortNamesQueryOptions);
const titleProvidersByShortName = computed(() =>
	(titleProvidersByShortNamesData.value?.packages || []).map(({ shortName }) => shortName)
);

const { mutate } = useMutation<ReportFeedbackMutation, ReportFeedbackMutationVariables>(ReportFeedbackDocument);

const issues = computed(() => {
	const object_type = titleObjectTypeTranslation.value;
	const object_type_capitalized = capitalize(titleObjectTypeTranslation.value);

	// the keys are values expected from the backend
	// translation keys need to be present in whole for automatic cleaning
	return [
		{
			technical_name: 'mismatch',
			translatable: t('WEBAPP_REPORT_ISSUE_MISMATCH', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'title_not_available',
			translatable: t('WEBAPP_REPORT_ISSUE_MISSING_TITLE_NOT_AVAILABLE', {
				object_type,
				object_type_capitalized,
			}),
		},
		{
			technical_name: 'missing_offer',
			translatable: t('WEBAPP_REPORT_ISSUE_MISSING_OFFER', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'wrong_price',
			translatable: t('WEBAPP_REPORT_ISSUE_WRONG_PRICE', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'wrong_info',
			translatable: t('WEBAPP_REPORT_ISSUE_METADATA', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'bug',
			translatable: t('WEBAPP_REPORT_ISSUE_BUG', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'cant_watch',
			translatable: t('WEBAPP_REPORT_ISSUE_CANT_WATCH', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'other',
			translatable: t('WEBAPP_REPORT_ISSUE_OTHER', { object_type, object_type_capitalized }),
		},
	];
});

const freetextPlaceholder = computed(() =>
	mandatoryComment.value ? tc('WEBAPP_REPORT_REQUIRED_FREETEXTPLACEHOLDER') : tc('WEBAPP_REPORT_FREETEXTPLACEHOLDER')
);

const mandatoryComment = computed(() => ['wrong_info', 'bug', 'other'].includes(ionItemData.issueSelected));

const translatedCountry = computed(() => tc(`WEBAPP_COUNTRY_${webLocale.value.toUpperCase()}`));

const titleObjectTypeTranslation = computed(() => {
	const objectTypeTranslations = {
		[ObjectType.Movie]: tc('WEBAPP_MOVIE'),
		[ObjectType.Show]: tc('WEBAPP_SHOW'),
		[ObjectType.ShowSeason]: tc('WEBAPP_SEASON'),
		[ObjectType.ShowEpisode]: tc('WEBAPP_EPISODE'),
		[ObjectType.SportEvent]: tc('WEBAPP_EVENT'),
	};

	return objectTypeTranslations[props.titleObjectType].toLowerCase();
});

const isFormInvalid = computed<boolean>(() => {
	const { issueSelected, stayUpdated, emailInput, commentInput, providerSelected, externalLinkInput } = ionItemData;
	if (issueSelected === 'missing_offer') {
		return externalLinkInput !== '' ? false : true;
	}

	if (mandatoryComment.value) {
		if (issueSelected && stayUpdated) {
			return emailInput !== '' && commentInput !== '' ? false : true;
		} else {
			return commentInput !== '' ? false : true;
		}
	} else {
		if (issueSelected && stayUpdated) {
			return emailInput !== '' && providerSelected !== '' ? false : true;
		} else {
			return providerSelected ? false : true;
		}
	}
});

const titleProviders = computed(() => {
	const items = (props.titleOffers ?? []) as (Offer | UpcomingRelease)[];
	const titleProviderIDs = items.map(offer => offer.package?.packageId).filter(Boolean);
	return providers.value().filter((provider: any) => titleProviderIDs.indexOf(provider.packageId) !== -1);
});

const filteredProviders = computed(() => {
	const missingProvider = {
		clearName: `(${tc('WEBAPP_REPORT_MISSINGPROVIDER')})`,
		icon: '',
		monetizationTypes: [],
		packageId: 0,
		shortName: '',
		slug: '',
		technicalName: 'missing',
	} as Provider;
	const sportProviders = providers
		.value()
		.filter((provider: Provider) => sportProvidersByShortName.value.includes(provider.shortName));
	const titleProvidersData = providers
		.value()
		.filter((provider: Provider) => titleProvidersByShortName.value.includes(provider.shortName));
	const filtered = props.titleObjectType === ObjectType.SportEvent ? sportProviders : titleProvidersData;
	const providersData =
		ionItemData.issueSelected === 'missing_offer' ? filtered.slice().sort() : [...titleProviders.value].sort();
	return [missingProvider].concat(providersData);
});

const selectedIssueTranslatable = computed(
	() => issues.value.find(el => el.technical_name === ionItemData.issueSelected)?.translatable || '...'
);

function triggerPopoverOpen(e: Event) {
	e.stopPropagation();
	(e.target as HTMLElement).parentElement?.click();
}

async function openPopover(event: Event) {
	const { popoverController } = await import('@/ionic.loader');

	if (!modal.value) return;

	const popover = await popoverController.create({
		cssClass: 'issues-popover',
		component: () => import('./IssuesListPopover.vue'),
		componentProps: {
			parent: modal.value,
			propsData: { issues: issues.value, selectedIssue: ionItemData.issueSelected },
		},
		event,
	});

	await popover.present();

	popover.onWillDismiss().then(res => {
		const selectedIssue = res.data;
		selectedIssue && handleIssueChange({ target: { value: selectedIssue } });
	});
}

function handleIssueChange(event?: any) {
	Object.assign(ionItemData, {
		commentInput: '',
		emailInput: '',
		errorNotification: false,
		externalLinkInput: '',
		isSending: false,
		providerSelected: '',
		providerSelectedClear: '',
		stayUpdated: false,
		issueSelected: event?.target?.value ?? '',
	});
}

async function openProviderPopover(event: Event) {
	const { popoverController } = await import('@/ionic.loader');

	if (!modal.value) return;

	const popover = await popoverController.create({
		cssClass: 'providers-popover',
		component: () => import('./ProviderListPopover.vue'),
		componentProps: {
			parent: modal.value,
			propsData: {
				providers: filteredProviders.value,
				selectedProvider: ionItemData.providerSelectedClear,
			},
		},
		event,
	});

	await popover.present();

	popover.onWillDismiss().then(res => {
		const selectedProvider = res.data;
		selectedProvider && handleProviderChange({ target: { value: selectedProvider } });
	});
}

function handleProviderChange(event: any) {
	const { technicalName, clearName } = event?.target?.value;

	ionItemData.providerSelected = technicalName ?? '';
	ionItemData.providerSelectedClear = clearName ?? '';
}

async function reportIssue() {
	if (ionItemData.isSending) return;

	ionItemData.isSending = true;

	const provider = providers.value().find((provider: Provider) => {
		return provider.technicalName === ionItemData.providerSelected;
	});

	try {
		if (ionItemData.emailInput) {
			const emailValid = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(ionItemData.emailInput);
			if (!emailValid) {
				ionItemData.errorNotification = true;
				ionItemData.isSending = false;
				return;
			}
		}

		await mutate({
			input: {
				id: props.titleId,
				// @ts-ignore
				type: issueName[ionItemData.issueSelected],
				country: country.value,
				language: language.value,
				package: provider?.shortName || '',
				comment: ionItemData.commentInput,
				externalUrl: ionItemData.externalLinkInput,
				email: ionItemData.emailInput,
				platformVersion: `${APP_VERSION}-${APP_PLATFORM}`,
			},
		});

		TrackingHelper.trackEvent('offer_reporter', {
			action: 'send_feedback',
			label: ionItemData.issueSelected ?? '',
		});

		// close current modal
		ModalHelper.close();
		// then open OfferReporterSuccessModal
		await ModalHelperVFM.openModal(
			OfferReporterSuccessModal,
			{ email: ionItemData.emailInput },
			{},
			'',
			false,
			props.additionalContexts
		);
	} catch (error) {
		ionItemData.errorNotification = true;
	}

	ionItemData.isSending = false;
}

function capitalize(str: string) {
	return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

return { __sfc: true,issueName, props, t, tc, providers, language, country, webLocale, modal, ionItemData, queryVariables, sportProvidersByShortNamesQueryOptions, sportProvidersByShortNamesData, sportProvidersByShortName, titleProvidersByShortNamesQueryOptions, titleProvidersByShortNamesData, titleProvidersByShortName, mutate, issues, freetextPlaceholder, mandatoryComment, translatedCountry, titleObjectTypeTranslation, isFormInvalid, titleProviders, filteredProviders, selectedIssueTranslatable, triggerPopoverOpen, openPopover, handleIssueChange, openProviderPopover, handleProviderChange, reportIssue, capitalize, faCaretDown, faInfoCircle, BasicButton, BaseModal }
}

})